<template>
  <base-dialog
    :title="$t('common.action.send') + ' ' + $t('common.action.paymentLink')"
    :actionName="$t('common.action.send')"
    @action="sendPaymentLink()"
    :actionDisabled="isValid()"
    :max-width="$vuetify.display.mdAndUp ? '30%' : '100%'"
    :showCancel="$t('common.action.cancel')"
    @close="showDialog = false"
    :isLoading="isLoading"
    :show="showDialog"
  >
    <template #content>
      <div class="ma-4">
        {{ $t("common.action.paymentLink") }}
        <v-combobox
          :disabled="usePaymentLinksStore().loading"
          v-model="paymentLinkSelect"
          :loading="usePaymentLinksStore().loading"
          :items="paymentLinks"
          :item-title="itemTitle"
          item-value="item"
          :placeholder="$t('page.customer.sendToCustomer.searchLinks')"
        >
          <template v-slot:append-item>
            <v-divider class="mt-2" />
            <v-list-item class="d-flex justify-center">
              <v-btn v-if="hasMore" color="primary" variant="flat" class="ma-4" @click="loadMore">
                {{ $t("page.customer.sendToCustomer.loadMoreAndContinue") }}
              </v-btn>
              <span class="text-grey" v-else>
                {{ $t("page.customer.sendToCustomer.allPaymentLinksLoaded") }}
              </span>
            </v-list-item>
          </template>
        </v-combobox>
        {{ $t("common.action.phone") }}
        <v-text-field
          disabled
          v-model="phone"
          :placeholder="$t('common.action.phone')"
          variant="outlined"
        />
        {{ $t("common.action.email") }}
        <v-text-field
          disabled
          v-model="email"
          :placeholder="$t('common.action.email')"
          variant="outlined"
        />
        <v-row>
          <v-col cols="6">
            <v-checkbox
              v-model="smsSend"
              :label="$t('common.action.send') + ' ' + $t('common.action.sms')"
              color="primary"
              density="comfortable"
              :disabled="!phone"
            />
          </v-col>
          <v-col cols="6">
            <v-checkbox
              v-model="emailSend"
              :disabled="!email"
              :label="$t('common.action.send') + ' ' + $t('common.action.email')"
              color="primary"
              density="comfortable"
            />
          </v-col>
        </v-row>
      </div>
    </template>
  </base-dialog>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import BaseDialog from "@/shared/components/Common/BaseDialog.vue";
import { useValidation } from "@/shared/composables/validation";
import { useCustomersStore } from "@/customers/stores/customers";
import { usePaymentLinksStore } from "@/payment-links/stores/paymentLinks";
import { Methods } from "@/shared/enums/twilio.enum";
import i18n from "@/app/plugins/i18n";
import { useAccountStore } from "@/account/stores/account";
import type { PaymentLinks } from "@/payment-links/types/paymentLinks";
import type { Customer } from "@/customers/types/customer";

const showDialog = ref(false);
const isLoading = ref(false);
const smsSend = ref(false);
const emailSend = ref(false);
const paymentLinks = ref<PaymentLinks[]>([]);
const paymentLinkSelect = ref<PaymentLinks | null>(null);
const paymentLinkName = ref("");
const customer = ref<Customer | undefined>(undefined);
const { email, phone } = useValidation();
const hasMore = ref(false);
const businessName = useAccountStore().account?.business_profile?.name ?? undefined;
const loadMore = async () => {
  const lastLinkListed = paymentLinks.value[paymentLinks.value.length - 1];
  const links = await usePaymentLinksStore().listActivePaymentLinks({
    limit: 50,
    starting_after: lastLinkListed?.id,
  });
  if (!links) {
    return;
  }
  paymentLinks.value = [...paymentLinks.value, ...links.data];
  hasMore.value = links.has_more;
};

const reset = () => {
  paymentLinkSelect.value = null;
  paymentLinkName.value = "";
  isLoading.value = false;
  smsSend.value = false;
  emailSend.value = false;
  hasMore.value = false;
};

const open = async (id?: string) => {
  reset();
  showDialog.value = true;
  if (id) {
    customer.value = await useCustomersStore().retrieveCustomer(id);
    phone.value = customer.value?.phone ?? "";
    email.value = customer.value?.email ?? "";
    const links = await usePaymentLinksStore().listActivePaymentLinks({
      limit: 50,
    });
    if (!links) {
      return;
    }
    paymentLinks.value = links.data;
    hasMore.value = links.has_more;
  }
};

defineExpose({
  open,
});

const isValid = () => {
  return !(paymentLinkSelect.value && (smsSend.value || emailSend.value));
};

// methods
const itemTitle = (item: PaymentLinks) => {
  return item.metadata?.name || item.id;
};
const sendPaymentLink = async () => {
  isLoading.value = true;
  // Send the payment link
  if (smsSend.value) {
    await usePaymentLinksStore().sendPaymentLink({
      name: paymentLinkSelect.value?.metadata?.name || i18n.global.t("common.action.paymentLink"),
      phone: phone.value ?? undefined,
      paymentLink: paymentLinkSelect.value?.url,
      methods: Methods.SMS,
      businessName,
    });
  }
  if (emailSend.value) {
    await usePaymentLinksStore().sendPaymentLink({
      name: paymentLinkSelect.value?.metadata?.name || i18n.global.t("common.action.you"),
      email: email.value ?? undefined,
      paymentLink: paymentLinkSelect.value?.url,
      methods: Methods.EMAIL,
      businessName,
    });
  }
  showDialog.value = false;
  isLoading.value = false;
};
</script>
